import React, { useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Editor
} from 'slate';
import {
  ReactEditor
} from 'slate-react';
import { setAttr } from 'redom';

import useOnEscapeKey from 'generics/hooks/on-escape-key';
import useOnClickOutside from 'generics/hooks/on-click-outside';

import application from 'application';

import {
  displayElementAroundAnchor
} from 'utilities/element-position';

import HelptapEditor from '../helpers';

import NodeMenu from '../node-menu';
import MoreNodeActionsMenu from '../more-node-actions-menu/react';

import { ReactComponent as AskAIIcon } from 'assets/images/ask-ai-icon.svg';

import './index.scss';

function DragHandleMenu(props) {

  const mainReference = useRef(null);
  const containerReference = useRef(null);

  function findBlockHandle() {

    try {
      const node = Editor.node(props.stylingMenu.editor, props.stylingMenu.elementPath)[0];
      return ReactEditor.toDOMNode(props.stylingMenu.editor, node).closest('.editor-node-main').querySelector(':scope > .node-handles-container .editor-node-control-button');
    } catch (exception) {
      return null;
    }

  }

  // Escape hands focus back to the block's handle, so the keyboard stays on that block. A focused
  // handle stays visible, which keeps a layout reached with Select layout ready to drag.
  function handleEscape(event) {

    const handle = findBlockHandle();

    props.onClose(event);

    if (handle !== null) {
      handle.focus();
    }

  }

  useOnEscapeKey(handleEscape);
  useOnClickOutside(containerReference, props.onClose);

  useLayoutEffect(function onLoad() {

    positionMenu();

    return function onUnload() {

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getDOMElementByNodePath(editor, nodePath) {
    try {
      const node = ReactEditor.toDOMNode(editor, Editor.node(editor, nodePath)[0]);
      return node;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error getting DOM node:', error, nodePath, editor.selection);
      return null;
    }
  }

  function positionMenu() {

    const elementPath = props.stylingMenu.elementPath;

    if (elementPath.length === 0) {
      return;
    }

    const editorDOMNode = getDOMElementByNodePath(props.stylingMenu.editor, elementPath);

    if (editorDOMNode === null) {
      return;
    }

    const element = containerReference.current;
    const anchorElement = editorDOMNode.closest('.editor-node-main').querySelector('.editor-node-control-button');
    const viewportElement = void 0;
    const verticalOffset = 0;
    const horizontalOffset = 0;
    const startSide = 'left';
    const placeOnCenter = true;
    const positionableSides = ['left'];

    setAttr(element, {
      style: {
        height: 'auto'
      }
    });

    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);
    displayElementAroundAnchor(element, anchorElement, viewportElement, verticalOffset, horizontalOffset, startSide, placeOnCenter, positionableSides);

  }

  function tryRenderSettingsMenu() {

    const editor = props.stylingMenu.editor;
    const elementPath = props.stylingMenu.elementPath;

    if (props.enableAdvancedOptions !== true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionDescendant(editor, elementPath) === true) {
      return;
    }

    if (HelptapEditor.dataCollection.isDataCollectionRowPageNodeDescendant(editor, elementPath) === true) {
      return;
    }

    return <div id="block-settings-menu-options" className="drag-handle-menu-section">
      <label className="drag-handle-menu-label">Block Settings</label>
      <MoreNodeActionsMenu {...props.settingsMenu} positionToolbar={positionMenu} />
    </div>;

  }

  function renderAssistantButton() {

    const properties = {
      className: 'node-menu-assistant-button',
      onClick() {
        props.onClose();
        setTimeout(() => application.assistantManager.assistant.focus(), 10);
      }
    };

    return <button {...properties}><AskAIIcon/><span>Assistant</span></button>;

  }

  function renderMain() {

    const mainAttributes = {
      id: 'drag-handle-menu-main',
      ref: mainReference,
      onClick(event) {
        if (event.target !== mainReference.current) {
          return;
        }
        event.stopPropagation();
        props.onClose();
      }
    };

    const containerAttributes = {
      id: 'drag-handle-menu-container',
      className: ' hide-scrollbar',
      ref: containerReference
    };

    return ReactDOM.createPortal(<div {...mainAttributes}>
      <div {...containerAttributes}>
        <div id="block-styling-menu-options" className="drag-handle-menu-section">
          <label className="drag-handle-menu-label">Block Styling</label>
          {renderAssistantButton()}
          <NodeMenu {...props.stylingMenu} positionToolbar={positionMenu} />
        </div>
        {tryRenderSettingsMenu()}
      </div>
    </div>, document.body);

  }

  return renderMain();

}

export default DragHandleMenu;